import { panelDataChip } from '@/components/layout/Panel'
import EmojiGlyph from '@/components/ui/EmojiGlyph'
import { useLanguage } from '@/contexts/LanguageContext'
import { saveMyPositions, useMyPositions } from '@/lib/myPositions'

/** Üyenin kendi pozisyon seçimi. Kaydet düğmesi yok: her dokunuş yazılır. */
export default function MyPositions() {
  const { T } = useLanguage()
  const { positions, options, loaded, saveFailed } = useMyPositions()
  const picked = new Map(positions.map(p => [p.id, p]))

  const toggle = (id: number) => {
    saveMyPositions(options.flatMap(o => {
      const isPicked = picked.has(o.id)
      if (o.id === id) return isPicked ? [] : [{ ...o, specs: [] }]
      return isPicked ? [picked.get(o.id)!] : []
    }))
  }

  const toggleSpec = (id: number, specName: string) => {
    saveMyPositions(positions.map(p => {
      if (p.id !== id) return p
      const option = options.find(o => o.id === id)
      const names = new Set(p.specs.map(s => s.name))
      if (names.has(specName)) names.delete(specName)
      else names.add(specName)
      return { ...p, specs: (option?.specs ?? []).filter(s => names.has(s.name)) }
    }))
  }

  if (!loaded) return null

  const withSpecs = options.filter(o => picked.has(o.id) && o.specs.length > 0)

  return (
    <>
      {options.length === 0 ? (
        <p className="text-xs text-muted-foreground/60">{T.members.positions.noOptions}</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {options.map(o => (
            <button
              key={o.id}
              type="button"
              aria-pressed={picked.has(o.id)}
              onClick={() => toggle(o.id)}
              className={panelDataChip(picked.has(o.id))}
            >
              <EmojiGlyph emoji={o.emoji} size={14} title={o.name} />
              {o.name}
            </button>
          ))}
        </div>
      )}

      {/* Alt sınıflar yalnızca seçili mevkide açılır: hepsi baştan görünseydi
          yirmiye yakın düğme arasında hangisinin hangi mevkiye ait olduğu
          okunmazdı. */}
      {withSpecs.length > 0 && (
        <div className="mt-3 space-y-2 border-t border-white/10 pt-3">
          <p className="text-[11px] text-muted-foreground/70">{T.members.positions.specsHint}</p>
          {withSpecs.map(o => {
            const chosen = new Set(picked.get(o.id)!.specs.map(s => s.name))
            return (
              <div key={o.id} role="group" aria-label={o.name} className="flex flex-wrap items-center gap-1.5">
                <span className="mr-1 flex items-center gap-1.5 text-xs text-foreground/85 sm:w-44">
                  <EmojiGlyph emoji={o.emoji} size={14} title={o.name} />
                  <span className="truncate">{o.name}</span>
                </span>
                {o.specs.map(s => (
                  <button
                    key={s.name}
                    type="button"
                    aria-pressed={chosen.has(s.name)}
                    onClick={() => toggleSpec(o.id, s.name)}
                    className={panelDataChip(chosen.has(s.name))}
                  >
                    <EmojiGlyph emoji={s.emoji} size={14} title={s.name} />
                    {s.name}
                  </button>
                ))}
              </div>
            )
          })}
        </div>
      )}

      {saveFailed && <p className="mt-2 text-xs text-destructive">{T.members.positions.saveError}</p>}
    </>
  )
}
